import { useState } from 'react';
import type { Citation } from '@/types';

interface CitationListProps {
  citations: Citation[];
}

/**
 * Compact citation markers with hover tooltips.
 * Shows [1] [2] [3] pills that reveal source info on hover.
 */
export function CitationList({ citations }: CitationListProps) {
  if (citations.length === 0) {
    return null;
  }

  // Filter to top citations (above 40% confidence) and limit to 5
  const topCitations = citations
    .filter(c => c.confidence >= 0.4)
    .slice(0, 5);

  if (topCitations.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex items-center gap-1 flex-wrap">
      <span className="text-xs text-muted-foreground mr-1">Sources:</span>
      {topCitations.map((citation, index) => (
        <CitationMarker
          key={`${citation.url}-${index}`}
          citation={citation}
          index={index + 1}
        />
      ))}
    </div>
  );
}

interface CitationMarkerProps {
  citation: Citation;
  index: number;
}

function CitationMarker({ citation, index }: CitationMarkerProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const sourceTypeLabels: Record<Citation['sourceType'], string> = {
    kb_article: 'KB Article',
    api_docs: 'API Docs',
    release_note: 'Release Note',
    product_page: 'Product Page',
    pricing_page: 'Pricing',
  };

  const percentage = Math.round(citation.confidence * 100);

  return (
    <div className="relative inline-block">
      <a
        href={citation.url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer"
        title={citation.title}
      >
        {index}
      </a>

      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
          <div className="bg-popover border border-border rounded-lg shadow-lg p-2.5 w-64 text-sm">
            <div className="font-medium text-foreground line-clamp-2 mb-1">
              {citation.title}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={`px-1.5 py-0.5 rounded ${
                percentage >= 70 ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                percentage >= 50 ? 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' :
                'bg-red-500/10 text-red-600 dark:text-red-400'
              }`}>
                {percentage}%
              </span>
              <span className="px-1.5 py-0.5 rounded bg-secondary">
                {sourceTypeLabels[citation.sourceType]}
              </span>
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground truncate">
              {new URL(citation.url).hostname}
            </div>
            {/* Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
              <div className="border-8 border-transparent border-t-border" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-px border-[7px] border-transparent border-t-popover" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
